import React, { useEffect, useRef, useState } from "react";
import {
  Grid,
  Typography,
  Container,
  useMediaQuery,
  Box,
  Button,
  MenuItem,
  TextField,
  OutlinedInput,
  InputAdornment,
  Autocomplete,
} from "@mui/material";
import "../../styles/inputBox.css";
import Select from "@mui/material/Select";
import AiIcon from "../../assets/images/AiIcon.svg";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { InputLabel } from "@material-ui/core";
import { generateTextObject } from "../../utils/AIGenerateText";
// removed CircularProgress in favor of shared Loader for consistent button sizing
import { getRequest, postRequest, putRequest } from "../../api/apiServices";
import {
  getHiringManagerList,
  getJobByName,
  getCompany,
  updateJob,
} from "../../api/apiList";
import { useNavigate, useParams } from "react-router-dom";
import ConfirmationDialog from "../DailogBox";
import LoadingOverlay from "../../utils/LoadingOverlay";
import Loader from "../Loader";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import { fetchHiringManagers } from "../../store/hiringManagerSlice";
import { useAuth } from "../../hooks/useAuth";
import { roleBasedRedirection } from "../../utils/helper";
import { employmentTypeOptions, keyValueForCurrency, periodOptions } from "../../constant/VonqConstant";
import { VonqMapping } from "../../types/vonqMapping";
import {
  deriveRemoteWorkChoice,
  finalizeJobSpecVonqMapping,
  formatHoursPerWeekText,
  formatSalaryRangeText,
  hydrateSalaryFromSources,
  hydrateWeeklyHoursFromSources,
  JobSpecCompensationFields,
  mapAllowsRemoteWorkToWorkLocationRatio,
  mapRemoteChoiceToAllowsRemoteWork,
  normalizeContractTypeLabel,
  normalizeSalaryCurrency,
  normalizeWorkLocationRatio,
  REMOTE_WORK_OPTIONS,
  resolveEmploymentType,
} from "../../utils/contractTypeMapping";
import { VonqJobTitleField, VonqLocationField } from "./vonq";
import { sanitizeLocationText } from "./vonq/vonqFieldUtils";
import {
  buildJobProfileFirstSnapshot,
  isSelfEmittedFormUpdate,
} from "../../utils/jobSpecFormSync";

interface JobProfileFirstProps {
  onNext: () => void; // Function to navigate to the next component
  setFormData: (data: any) => void;
  initialData: any;
  mode: any;
}

interface Manager {
  id: string;
  name: string;
}

interface GeneratedTextResponse {
  [key: string]: string;
}

const JobProfileFirst: React.FC<JobProfileFirstProps> = ({
  onNext,
  setFormData,
  initialData,
  mode,
}: JobProfileFirstProps) => {
  const isMobile = useMediaQuery("(max-width:578px)");
  const isTabs = useMediaQuery("(max-width:1200px)");

  //get data from context
  const { user } = useAuth();

  // User Store
  const dispatch: AppDispatch = useDispatch<AppDispatch>();

  //params
  const params = useParams();
  const navigate = useNavigate();

  const goBack = () => {
    navigate(roleBasedRedirection("/jobs"));
  };

  //decalare form data — seed from parent formData so values survive step navigation
  const [jobTitle, setJobTitle] = useState(initialData?.jobTitle || "");
  const [hiringManagerId, setHiringManagerId] = useState(
    initialData?.hiringManagerId != null
      ? String(initialData.hiringManagerId)
      : ""
  );
  const [workingLocation, setWorkingLocation] = useState("");
  const [workLocationRatio, setWorkLocationRatio] = useState("");
  const [vacancyReason, setVacancyReason] = useState("");
  const [contractType, setContractType] = useState("");
  const [hoursPerWeek, setHoursPerWeek] = useState("");
  const [salaryRange, setSalaryRange] = useState("");
  const [weeklyHoursFrom, setWeeklyHoursFrom] = useState("");
  const [weeklyHoursTo, setWeeklyHoursTo] = useState("");
  const [salaryFrom, setSalaryFrom] = useState("");
  const [salaryTo, setSalaryTo] = useState("");
  const [salaryPeriod, setSalaryPeriod] = useState("yearly");
  const [salaryCurrency, setSalaryCurrency] = useState("");
  const [defaultCurrency, setDefaultCurrency] = useState("");
  const [benefitsWithThisRole, setBenefitsWithThisRole] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  const [otherVacancy, setOtherVacancy] = useState("");
  const [remoteWorkChoice, setRemoteWorkChoice] = useState<"" | "yes" | "no">(
    ""
  );

  const [vonqMapping, setVonqMapping] = useState<VonqMapping>(
    initialData?.vonqMapping || {}
  );

  const lastEmittedSnapshotRef = useRef<string | null>(null);
  const hasHydratedRef = useRef(false);
  const loadedJobIdRef = useRef<string | number | null>(null);
  const companyBenefitsPrefilledRef = useRef(false);

  const updateVonqMapping = (patch: Partial<VonqMapping>) => {
    setVonqMapping((prev) => ({
      ...prev,
      ...patch,
      syncSource: "job_spec_form",
      syncedAt: new Date().toISOString(),
    }));
  };

  const applyCompensationFromSources = (
    hoursText?: string,
    salaryText?: string,
    mapping?: VonqMapping,
    currencyDefault?: string,
    structured?: Pick<
      JobSpecCompensationFields,
      | "weeklyHoursFrom"
      | "weeklyHoursTo"
      | "salaryFrom"
      | "salaryTo"
      | "salaryPeriod"
      | "salaryCurrency"
    >
  ) => {
    const hours = hydrateWeeklyHoursFromSources(hoursText, mapping, structured);
    setWeeklyHoursFrom(hours.from);
    setWeeklyHoursTo(hours.to);
    setHoursPerWeek(formatHoursPerWeekText(hours.from, hours.to));

    const salary = hydrateSalaryFromSources(
      salaryText,
      mapping,
      currencyDefault || defaultCurrency,
      structured
    );
    setSalaryFrom(salary.from);
    setSalaryTo(salary.to);
    setSalaryPeriod(salary.period || "yearly");
    setSalaryCurrency(salary.currency);
    setSalaryRange(
      formatSalaryRangeText(
        salary.from,
        salary.to,
        salary.period,
        salary.currency
      )
    );
  };

  const buildCompensationPayload = () => {
    const legacyHours = formatHoursPerWeekText(weeklyHoursFrom, weeklyHoursTo);
    const legacySalary = formatSalaryRangeText(
      salaryFrom,
      salaryTo,
      salaryPeriod,
      salaryCurrency
    );

    return {
      hoursPerWeek: legacyHours,
      salaryRange: legacySalary,
      defaultCurrency: salaryCurrency || defaultCurrency,
      weeklyHoursFrom,
      weeklyHoursTo,
      salaryFrom,
      salaryTo,
      salaryPeriod,
      salaryCurrency,
    };
  };

  const vonqFieldProps = {
    fullWidth: true,
    sx: {
      "& .css-8ewcdo-MuiInputBase-root-MuiOutlinedInput-root": {
        padding: "12px 11px",
      },
    },
  };
  const vacantTypes = [
    "Replacement",
    "Expansion Team",
    "New Project",
    "Restructuring",
    "Other",
  ];

  const handleContractTypeChange = (event: any) => {
    const label = event.target.value;
    setContractType(label);
    const option = employmentTypeOptions.find((item) => item.label === label);
    updateVonqMapping({
      employmentType: option?.value || resolveEmploymentType(label),
    });

    if (label && label !== "Please select position") {
      setErrors((prevErrors) => ({
        ...prevErrors,
        contractType: "",
      }));
    }
  };

  const handleRemoteWorkChange = (value: "yes" | "no") => {
    setRemoteWorkChoice(value);
    setWorkLocationRatio(value === "yes" ? "Remote" : "On-site");
    updateVonqMapping({
      allowsRemoteWork: mapRemoteChoiceToAllowsRemoteWork(value),
    });
    setErrors((prevErrors) => ({
      ...prevErrors,
      workLocationRatio: "",
    }));
  };

  const handleVacancyReason = (event: any) => {
    const value = event.target.value;
    setVacancyReason(value);
    if (value !== "Other") {
      setOtherVacancy("");
    }

    if (value !== "Please select vacant postion") {
      setErrors((prevErrors) => ({
        ...prevErrors,
        vacancyReason: "",
      }));
    }
  };

  const [errors, setErrors] = useState({
    jobTitle: "",
    hiringManagerId: "",
    workingLocation: "",
    workLocationRatio: "",
    vacancyReason: "",
    contractType: "",
    hoursPerWeek: "",
    salaryRange: "",
    salaryCurrency: "",
    benefitsWithThisRole: "",
  });

  const prefillBenefitsFromCompanyProfile = async () => {
    if (!user?.uId || mode) {
      return;
    }

    try {
      const response = await getRequest(getCompany(user.uId));
      const companyData = response?.data?.data;
      const secondaryBenefits = companyData?.secondaryBenefits?.trim() || "";

      if (secondaryBenefits) {
        setBenefitsWithThisRole((current) =>
          current.trim() ? current : secondaryBenefits
        );
        companyBenefitsPrefilledRef.current = true;
      }

      const companyCurrency = companyData?.defaultCurrency;
      if (companyCurrency) {
        setDefaultCurrency(companyCurrency);
        setSalaryCurrency((current) => current || companyCurrency);
      }
    } catch (error) {
      console.error("Failed to prefill benefits from company profile:", error);
    }
  };

  useEffect(() => {
    dispatch(fetchHiringManagers());
    hasHydratedRef.current = true;
  }, [dispatch]);

  useEffect(() => {
    if (!mode && user?.uId) {
      prefillBenefitsFromCompanyProfile();
    }
  }, [user?.uId, mode]);

  // Sync local state with initialData when loading a job or external updates only
  useEffect(() => {
    if (!initialData || Object.keys(initialData).length === 0) {
      return;
    }

    const incomingSnapshot = buildJobProfileFirstSnapshot(initialData);
    if (isSelfEmittedFormUpdate(incomingSnapshot, lastEmittedSnapshotRef)) {
      hasHydratedRef.current = true;
      return;
    }

    const jobId = initialData.id as string | number | undefined;
    const isNewExternalJobLoad =
      jobId != null && loadedJobIdRef.current !== jobId;
    const shouldHydrate = isNewExternalJobLoad || !hasHydratedRef.current;

    if (!shouldHydrate) {
      return;
    }

    if (jobId != null) {
      loadedJobIdRef.current = jobId;
    }

    if (initialData?.jobTitle !== undefined) {
      setJobTitle(initialData.jobTitle || "");
    }
    if (initialData?.hiringManagerId !== undefined) {
      setHiringManagerId(
        initialData.hiringManagerId != null
          ? String(initialData.hiringManagerId)
          : ""
      );
    }
    if (initialData?.workingLocation !== undefined) {
      setWorkingLocation(sanitizeLocationText(initialData.workingLocation) || "");
    }
    if (initialData?.workLocationRatio !== undefined) {
      const ratioText = initialData.workLocationRatio || "";
      const allowsRemote = initialData?.vonqMapping?.allowsRemoteWork;
      setRemoteWorkChoice(deriveRemoteWorkChoice(ratioText, allowsRemote));
      setWorkLocationRatio(
        mapAllowsRemoteWorkToWorkLocationRatio(allowsRemote) ||
          normalizeWorkLocationRatio(ratioText) ||
          ratioText
      );
    }
    if (initialData?.vacancyReason !== undefined) {
      setVacancyReason(initialData.vacancyReason || "Please select vacant postion");
    }
    if (initialData?.contractType !== undefined) {
      setContractType(
        normalizeContractTypeLabel(initialData.contractType) ||
          "Please select position"
      );
    }
    if (
      initialData?.hoursPerWeek !== undefined ||
      initialData?.salaryRange !== undefined ||
      initialData?.weeklyHoursFrom !== undefined ||
      initialData?.salaryTo !== undefined ||
      initialData?.vonqMapping
    ) {
      applyCompensationFromSources(
        initialData?.hoursPerWeek,
        initialData?.salaryRange,
        initialData?.vonqMapping,
        initialData?.defaultCurrency || defaultCurrency,
        {
          weeklyHoursFrom: initialData?.weeklyHoursFrom,
          weeklyHoursTo: initialData?.weeklyHoursTo,
          salaryFrom: initialData?.salaryFrom,
          salaryTo: initialData?.salaryTo,
          salaryPeriod: initialData?.salaryPeriod,
          salaryCurrency: initialData?.salaryCurrency,
        }
      );
    }
    if (initialData?.defaultCurrency) {
      setDefaultCurrency(initialData.defaultCurrency);
      if (!salaryCurrency) setSalaryCurrency(initialData.defaultCurrency);
    }
    if (mode) {
      if (initialData?.benefitsWithThisRole !== undefined) {
        setBenefitsWithThisRole(initialData.benefitsWithThisRole || "");
      }
    } else if (initialData?.benefitsWithThisRole?.trim()) {
      setBenefitsWithThisRole(initialData.benefitsWithThisRole);
    } else if (user?.uId) {
      prefillBenefitsFromCompanyProfile();
    }
    if (initialData?.vonqMapping) {
      setVonqMapping(initialData.vonqMapping);
      setRemoteWorkChoice((current) =>
        current ||
        deriveRemoteWorkChoice(
          initialData.workLocationRatio || "",
          initialData.vonqMapping.allowsRemoteWork
        )
      );
      if (initialData.vonqMapping.employmentType && !initialData.contractType) {
        const option = employmentTypeOptions.find(
          (item) => item.value === initialData.vonqMapping.employmentType
        );
        if (option) setContractType(option.label);
      }
    }

    hasHydratedRef.current = true;
  }, [initialData]);

  // Keep parent formData in sync so sidebar navigation preserves step 1 fields
  useEffect(() => {
    if (!hasHydratedRef.current) {
      return;
    }

    const compensation = buildCompensationPayload();
    const currentData = {
      ...initialData,
      jobTitle,
      hiringManagerId,
      workingLocation,
      workLocationRatio,
      vacancyReason,
      contractType,
      benefitsWithThisRole,
      ...compensation,
      vonqMapping,
    };
    const snapshot = buildJobProfileFirstSnapshot(currentData);
    const parentSnapshot = buildJobProfileFirstSnapshot(initialData || {});

    if (snapshot === parentSnapshot) {
      return;
    }

    lastEmittedSnapshotRef.current = snapshot;
    setFormData(currentData);
  }, [jobTitle, hiringManagerId, workingLocation, workLocationRatio, remoteWorkChoice, vacancyReason, contractType, hoursPerWeek, salaryRange, weeklyHoursFrom, weeklyHoursTo, salaryFrom, salaryTo, salaryPeriod, salaryCurrency, benefitsWithThisRole, vonqMapping, initialData, setFormData]);

  const hiringManager = useSelector((state: RootState) => state.hiringManager);
  
  // Update hiringManagerName when hiringManager list or initialData changes
  useEffect(() => {
    if (hiringManager.length > 0 && (initialData?.hiringManagerId || hiringManagerId)) {
      const managerId = hiringManagerId || initialData?.hiringManagerId;
      const selectedManager = hiringManager.find(
        (hiring) =>
          hiring.id.toString() === managerId?.toString()
      );
      if (selectedManager) {
        setHiringManagerName(selectedManager.name);
      } else if (!managerId) {
        setHiringManagerName("Please select hiring manager");
      }
    }
  }, [hiringManager, initialData?.hiringManagerId, hiringManagerId]);

  const handleSubmit = async (event: any) => {
    event.preventDefault();
    let vacancyReasons = vacancyReason;
    if (vacancyReasons === "Other") {
      vacancyReasons = otherVacancy;
    }
    const compensation = buildCompensationPayload();
    const data = {
      jobTitle,
      hiringManagerId,
      workingLocation,
      workLocationRatio,
      vacancyReason: vacancyReasons,
      contractType,
      hoursPerWeek: compensation.hoursPerWeek,
      salaryRange: compensation.salaryRange,
      benefitsWithThisRole,
      defaultCurrency: compensation.defaultCurrency,
      weeklyHoursFrom: compensation.weeklyHoursFrom,
      weeklyHoursTo: compensation.weeklyHoursTo,
      salaryFrom: compensation.salaryFrom,
      salaryTo: compensation.salaryTo,
      salaryPeriod: compensation.salaryPeriod,
      salaryCurrency: compensation.salaryCurrency,
      vonqMapping: finalizeJobSpecVonqMapping({
        vonqMapping,
        contractType,
        workLocationRatio,
        ...compensation,
      }),
    };
    if (formIsValid()) {
      if (mode) {
        setLoading(true);
        const response = await putRequest(updateJob(params?.id), data);
        setFormData(response?.data?.data);
        setLoading(false);
        onNext();
      } else {
        const allData = {
          ...initialData,
          ...data,
        };
        setFormData(allData);
        onNext(); // Call the onNext function to navigate to the next component
      }
    }
  };

  // For Timing
  const [titleTimer, setTitleTimer] = useState<NodeJS.Timeout | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [foundJobData, setFoundJobData] = useState<any>(null);

  const handleJobTitleSelected = (selectedJobTitle: string) => {
    setJobTitle(selectedJobTitle);

    if (selectedJobTitle.trim() !== "") {
      setErrors((prevErrors) => ({
        ...prevErrors,
        jobTitle: "",
      }));
    }

    if (!mode && selectedJobTitle.trim()) {
      if (titleTimer) {
        clearTimeout(titleTimer);
      }

      setTitleTimer(
        setTimeout(async () => {
          try {
            const response = await postRequest(getJobByName, {
              name: selectedJobTitle,
            });
            if (response?.status === 200 && response?.data) {
              setFoundJobData(response.data.data);
              setOpenDialog(true);
            } else {
              setConfirmDialogOpen(true);
            }
          } catch (error) {
            console.error("Error in handleJobTitleSelected:", error);
          }
        }, 500)
      );
    }
  };

  const handleDialogConfirm = () => {
    if (foundJobData) {
      setBenefitsWithThisRole(foundJobData?.benefitsWithThisRole);
      applyCompensationFromSources(
        foundJobData?.hoursPerWeek,
        foundJobData?.salaryRange,
        foundJobData?.vonqMapping,
        foundJobData?.defaultCurrency || defaultCurrency
      );
      setContractType(normalizeContractTypeLabel(foundJobData?.contractType));
      setVacancyReason(foundJobData?.vacancyReason);
      setWorkingLocation(sanitizeLocationText(foundJobData?.workingLocation));
      if (foundJobData?.vonqMapping) {
        setVonqMapping(foundJobData.vonqMapping);
        setRemoteWorkChoice(
          deriveRemoteWorkChoice(
            foundJobData?.workLocationRatio || "",
            foundJobData.vonqMapping.allowsRemoteWork
          )
        );
        setWorkLocationRatio(
          mapAllowsRemoteWorkToWorkLocationRatio(
            foundJobData.vonqMapping.allowsRemoteWork
          ) ||
            normalizeWorkLocationRatio(foundJobData?.workLocationRatio || "") ||
            foundJobData?.workLocationRatio ||
            ""
        );
      } else {
        setWorkLocationRatio(
          normalizeWorkLocationRatio(foundJobData?.workLocationRatio || "")
        );
        setRemoteWorkChoice(
          deriveRemoteWorkChoice(foundJobData?.workLocationRatio || "")
        );
      }
      initialData.taskOfThisPosition = foundJobData?.taskOfThisPosition;
      initialData.anyProjectsOfCandidates =
        foundJobData?.anyProjectsOfCandidates;
      initialData.educationAndExperience = foundJobData?.educationAndExperience;
      initialData.technicalSkillsRequired =
        foundJobData?.technicalSkillsRequired;
      initialData.softSkillsRequired = foundJobData?.softSkillsRequired;
      initialData.preferredCertificationsLicenses =
        foundJobData?.preferredCertificationsLicenses;
      initialData.idealCandidateProfile = foundJobData?.idealCandidateProfile;
      initialData.idealExperienceLevel = foundJobData?.idealExperienceLevel;
      initialData.industrySpecificExperienceRequired =
        foundJobData?.industrySpecificExperienceRequired;
      initialData.roleChallenges = foundJobData?.roleChallenges;
      initialData.organizationalGrowthOpportunities =
        foundJobData?.organizationalGrowthOpportunities;
      initialData.positionGoals = foundJobData?.positionGoals;
      initialData.successMetrics = foundJobData?.successMetrics;
      initialData.teamInteraction = foundJobData?.teamInteraction;

      initialData.reportingStructure = foundJobData?.reportingStructure;
      initialData.cultureFit = foundJobData?.cultureFit;
      initialData.positionTimeline = foundJobData?.positionTimeline;
      initialData.urgentNeeds = foundJobData?.urgentNeeds;
      initialData.interviewProcess = foundJobData?.interviewProcess;
      initialData.additionalRequirements = foundJobData?.additionalRequirements;
    }
    setOpenDialog(false);
  };

  const handleDialogCancel = async () => {
    setOpenDialog(false);
    if (!mode) {
      try {
        setAiLoading(true);
        const object = {
          // vacancyReason: [
          //   `Title: ${jobTitle} Question: What is the reason why this position is vacant?`,
          // ],

                  educationAndExperience: [
    `You are an expert career assistant AI.

Your task: Generate precise education and experience requirements strictly based on the job title provided.

Job Title: ${jobTitle}

Question: "What are the essential qualifications both in terms of education and experience?"

Output Format (follow exactly):
- Education: [One key academic qualification strongly related to this job]
- Experience: [Typical years and type of relevant experience]

Rules:
1. Use information ONLY related to the given job title.
2. Do NOT change, interpret, or replace the job title.
3. Keep it concise, complete, and professional.
4. Do not include extra formatting, symbols, or emojis.
5. Do not cut or truncate any sentence.`
  ],


      technicalSkillsRequired: [
    `You are an expert career assistant AI.

Your task: List technical skills strictly related to the given job title.

Job Title: ${jobTitle}

Question: "Are there any specific technical skills required for this role?"

Output Format:
- Technical Skills: [One-line list of essential technical skills]

Rules:
1. Use skills ONLY relevant to the given job title.
2. Do NOT reinterpret or substitute the job title.
3. Keep it clean and concise.
4. Provide a complete and well-formed response without truncation.`
  ],


        softSkillsRequired: [
    `You are an expert career assistant AI.

Your task: List soft skills strictly relevant to the provided job title.

Job Title: ${jobTitle}

Question: "What soft skills are you looking for in the candidate?"

Output Format:
- Soft Skills: [One-line list of key soft skills]

Rules:
1. Use soft skills that match the given job.
2. Do NOT modify or assume a different job title.
3. Keep the response clean, complete, and coherent.
4. No symbols, emojis, or truncated sentences.`
  ],


  preferredCertificationsLicenses: [
    `You are an expert career assistant AI.

Your task: Provide certifications or licenses relevant to the job title.

Job Title: ${jobTitle}

Question: "Are there preferred certifications or licenses?"

Output Format:
- Certifications/Licenses: [One or two relevant certifications]

Rules:
1. Certifications must directly match the job title.
2. Do NOT invent or imply different roles.
3. Keep formatting minimal and professional.
4. No missing or cut sentences.`
  ],


      roleChallenges: [
    `You are an expert career assistant AI.

Your task: Provide challenges that are realistic for this specific job title.

Job Title: ${jobTitle}

Question: "What challenges might the candidate face in this role?"

Output Format:
- Bullet points listing key challenges.

Rules:
1. Do NOT repeat or alter the job title.
2. Challenges must match the job title exactly.
3. Write full, clear sentences.
4. No incomplete or cut-off responses.`
  ],


     organizationalGrowthOpportunities: [
    `You are an expert career assistant AI.

Your task: Describe growth opportunities within the organization for this job title.

Job Title: ${jobTitle}

Question: "What growth opportunities are available within the organization?"

Output Format:
- Bullet points or a concise one-liner about career progression.

Rules:
1. Do NOT mention or repeat the job title.
2. Ensure growth opportunities align with the job type.
3. Keep it clear, simple, and complete.
4. No symbols, emojis, or truncated content.`
  ],

  successMetrics: [
    `You are an expert career assistant AI.

Your task: Explain how success is measured in this role.

Job Title: ${jobTitle}

Question: "How will success be measured in this role?"

Output Format:
- Bullet points or concise measurements of role success.

Rules:
1. Do NOT repeat the job title.
2. Ensure success metrics match the job responsibilities.
3. Keep it professional and complete.
4. Avoid truncated or incomplete answers.`
  ],


      teamInteraction: [
    `You are an expert career assistant AI.

Your task: Describe how the team is structured for this role.

Job Title: ${jobTitle}

Question: "How is the team structured for this role?"

Output Format:
- Bullet points or one concise line describing team structure.

Rules:
1. Do NOT mention or reinterpret the job title.
2. Keep it accurate and relevant to the actual role.
3. Ensure clear, complete statements only.`
  ],


    cultureFit: [
    `You are an expert career assistant AI.

Your task: Provide a concise culture fit description for this job title.

Job Title: ${jobTitle}

Question: "What is the team culture like, and what attributes should the candidate possess to fit in well?"

Rules:
1. Up to 300 tokens.
2. Do NOT mention or repeat the job title.
3. Focus on attributes that match this job type.
4. Provide complete, well-formed sentences with no truncation.`
  ],

        };
        const data = (await generateTextObject(
          object
        )) as GeneratedTextResponse;
        // setVacancyReason(data?.vacancyReason);
        initialData.educationAndExperience = data?.educationAndExperience;
        initialData.technicalSkillsRequired = data?.technicalSkillsRequired;
        initialData.softSkillsRequired = data?.softSkillsRequired;
        initialData.preferredCertificationsLicenses =
          data?.preferredCertificationsLicenses;
        initialData.roleChallenges = data?.roleChallenges;
        initialData.organizationalGrowthOpportunities =
          data?.organizationalGrowthOpportunities;
        initialData.successMetrics = data?.successMetrics;
        initialData.teamInteraction = data?.teamInteraction;
        initialData.cultureFit = data?.cultureFit;
        setAiLoading(false);
      } catch (error) {
        console.error("Error in generating AI data:", error);
      }
    }
  };

  const handleConfirmGenerate = async () => {
    setConfirmDialogOpen(false); // Close confirmation dialog
    setAiLoading(true);
    const object = {
      // vacancyReason: [
      //   `Title: ${jobTitle} Question: What is the reason why this position is vacant?`,
      // ],

        educationAndExperience: [
    `You are an expert career assistant AI.

Your task: Generate precise education and experience requirements strictly based on the job title provided.

Job Title: ${jobTitle}

Question: "What are the essential qualifications both in terms of education and experience?"

Output Format (follow exactly):
- Education: [One key academic qualification strongly related to this job]
- Experience: [Typical years and type of relevant experience]

Rules:
1. Use information ONLY related to the given job title.
2. Do NOT change, interpret, or replace the job title.
3. Keep it concise, complete, and professional.
4. Do not include extra formatting, symbols, or emojis.
5. Do not cut or truncate any sentence.`
  ],


      technicalSkillsRequired: [
    `You are an expert career assistant AI.

Your task: List technical skills strictly related to the given job title.

Job Title: ${jobTitle}

Question: "Are there any specific technical skills required for this role?"

Output Format:
- Technical Skills: [One-line list of essential technical skills]

Rules:
1. Use skills ONLY relevant to the given job title.
2. Do NOT reinterpret or substitute the job title.
3. Keep it clean and concise.
4. Provide a complete and well-formed response without truncation.`
  ],


        softSkillsRequired: [
    `You are an expert career assistant AI.

Your task: List soft skills strictly relevant to the provided job title.

Job Title: ${jobTitle}

Question: "What soft skills are you looking for in the candidate?"

Output Format:
- Soft Skills: [One-line list of key soft skills]

Rules:
1. Use soft skills that match the given job.
2. Do NOT modify or assume a different job title.
3. Keep the response clean, complete, and coherent.
4. No symbols, emojis, or truncated sentences.`
  ],


  preferredCertificationsLicenses: [
    `You are an expert career assistant AI.

Your task: Provide certifications or licenses relevant to the job title.

Job Title: ${jobTitle}

Question: "Are there preferred certifications or licenses?"

Output Format:
- Certifications/Licenses: [One or two relevant certifications]

Rules:
1. Certifications must directly match the job title.
2. Do NOT invent or imply different roles.
3. Keep formatting minimal and professional.
4. No missing or cut sentences.`
  ],


      roleChallenges: [
    `You are an expert career assistant AI.

Your task: Provide challenges that are realistic for this specific job title.

Job Title: ${jobTitle}

Question: "What challenges might the candidate face in this role?"

Output Format:
- Bullet points listing key challenges.

Rules:
1. Do NOT repeat or alter the job title.
2. Challenges must match the job title exactly.
3. Write full, clear sentences.
4. No incomplete or cut-off responses.`
  ],


     organizationalGrowthOpportunities: [
    `You are an expert career assistant AI.

Your task: Describe growth opportunities within the organization for this job title.

Job Title: ${jobTitle}

Question: "What growth opportunities are available within the organization?"

Output Format:
- Bullet points or a concise one-liner about career progression.

Rules:
1. Do NOT mention or repeat the job title.
2. Ensure growth opportunities align with the job type.
3. Keep it clear, simple, and complete.
4. No symbols, emojis, or truncated content.`
  ],

  successMetrics: [
    `You are an expert career assistant AI.

Your task: Explain how success is measured in this role.

Job Title: ${jobTitle}

Question: "How will success be measured in this role?"

Output Format:
- Bullet points or concise measurements of role success.

Rules:
1. Do NOT repeat the job title.
2. Ensure success metrics match the job responsibilities.
3. Keep it professional and complete.
4. Avoid truncated or incomplete answers.`
  ],


      teamInteraction: [
    `You are an expert career assistant AI.

Your task: Describe how the team is structured for this role.

Job Title: ${jobTitle}

Question: "How is the team structured for this role?"

Output Format:
- Bullet points or one concise line describing team structure.

Rules:
1. Do NOT mention or reinterpret the job title.
2. Keep it accurate and relevant to the actual role.
3. Ensure clear, complete statements only.`
  ],


    cultureFit: [
    `You are an expert career assistant AI.

Your task: Provide a concise culture fit description for this job title.

Job Title: ${jobTitle}

Question: "What is the team culture like, and what attributes should the candidate possess to fit in well?"

Rules:
1. Up to 300 tokens.
2. Do NOT mention or repeat the job title.
3. Focus on attributes that match this job type.
4. Provide complete, well-formed sentences with no truncation.`
  ],

    };

    try {
      const data = (await generateTextObject(object)) as GeneratedTextResponse;
      // setVacancyReason(data?.vacancyReason);
      initialData.educationAndExperience = data?.educationAndExperience;
      initialData.technicalSkillsRequired = data?.technicalSkillsRequired;
      initialData.softSkillsRequired = data?.softSkillsRequired;
      initialData.preferredCertificationsLicenses =
        data?.preferredCertificationsLicenses;
      initialData.roleChallenges = data?.roleChallenges;
      initialData.organizationalGrowthOpportunities =
        data?.organizationalGrowthOpportunities;
      initialData.successMetrics = data?.successMetrics;
      initialData.teamInteraction = data?.teamInteraction;

      initialData.cultureFit = data?.cultureFit;
      setAiLoading(false);
    } catch (error) {
      console.error("Error generating AI data:", error);
      setAiLoading(false);
    }
  };

  const formIsValid = () => {
    const newErrors = {
      jobTitle: !jobTitle?.trim()
        ? "Job Title is required"
        : !vonqMapping?.jobTitle?.vonqId
          ? "Please select a job title from the list"
          : "",
      hiringManagerId: !hiringManagerId ? "Hiring Manager is required" : "",
      workingLocation: !workingLocation?.trim()
        ? "Working Location is required"
        : !vonqMapping?.location?.vonqId
          ? "Please select a location from the list"
          : "",
      workLocationRatio: !remoteWorkChoice
        ? "Please select whether remote work is allowed"
        : "",
      vacancyReason:
        vacancyReason === "Please select vacant postion" ||
        !vacancyReason?.trim()
          ? "Vacancy Reason is required"
          : "",
      contractType:
        contractType === "Please select position" || !contractType?.trim()
          ? "Contract Type is required"
          : "",
      hoursPerWeek:
        !weeklyHoursFrom.trim() && !weeklyHoursTo.trim()
          ? "Hours is required"
          : "",
      salaryRange: !salaryTo.trim() ? "Salary range (to) is required" : "",
      salaryCurrency:
        salaryTo.trim() &&
        !normalizeSalaryCurrency(salaryCurrency, defaultCurrency)
          ? "Currency is required when a salary is provided"
          : "",
      benefitsWithThisRole: "",
    };

    setErrors(newErrors);

    return Object.values(newErrors).every((error) => error === "");
  };

  // DropDown
  const [hiringManagerName, setHiringManagerName] = useState(
    "Please select hiring manager"
  );

  const handleSelectChange = (e: any) => {
    const selectedManagerId = String(e.target.value);
    const selectedManager = hiringManager.find(
      (manager) => String(manager.id) === selectedManagerId
    );
    if (selectedManager) {
      setHiringManagerId(selectedManager.id.toString());
      setHiringManagerName(selectedManager?.name);

      setErrors((prevErrors) => ({
        ...prevErrors,
        hiringManagerId: "",
      }));
    }
  };

  return (
    <div>
      <Container
        style={{
          display: "flex",
          padding: "25px",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Grid
          spacing={2}
          bgcolor="#FFFFFF"
          width="884px"
          borderRadius="22px"
          border="1px solid #D6D6D7"
          margin={0}
        >
          <Grid
            item
            height={isMobile ? "auto" : "auto"}
            style={{ padding: 30 }}
          >
            <Grid
              container
              direction="row"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              gap="10px"
            >
              <Grid
                item
                width="fit-content"
                display="flex"
                alignItems="center"
                gap="10px"
              >
                <ArrowBackIcon onClick={goBack} style={{ cursor: "pointer" }} />
                <Typography color="#273848" fontWeight={700} fontSize="20.94px">
                  Overview & Job features
                </Typography>
                <Grid mb={1}></Grid>
              </Grid>
              <Grid item gap={2} display="flex">
                <Box
                  sx={{
                    width: 36,
                    height: 8,
                    borderRadius: 4,
                    bgcolor: "#84E1CC",
                  }}
                />
                <Box
                  sx={{
                    width: 36,
                    height: 8,
                    borderRadius: 4,
                    bgcolor: "#DFF4EF",
                  }}
                />
                <Box
                  sx={{
                    width: 36,
                    height: 8,
                    borderRadius: 4,
                    bgcolor: "#DFF4EF",
                  }}
                />
                <Box
                  sx={{
                    width: 36,
                    height: 8,
                    borderRadius: 4,
                    bgcolor: "#DFF4EF",
                  }}
                />
                <Box
                  sx={{
                    width: 36,
                    height: 8,
                    borderRadius: 4,
                    bgcolor: "#DFF4EF",
                  }}
                />
                <Box
                  sx={{
                    width: 36,
                    height: 8,
                    borderRadius: 4,
                    bgcolor: "#DFF4EF",
                  }}
                />
              </Grid>
            </Grid>
            <Grid my={4}></Grid>
            <Grid item>
              <Typography className="input-label-custom">
                Add hiring manager to the job{" "}
                <span style={{ color: "red" }}>*</span>
              </Typography>
              <Grid mb={1}></Grid>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={hiringManagerId ? String(hiringManagerId) : ""}
                fullWidth
                displayEmpty
                onChange={(e) => handleSelectChange(e)}
                renderValue={(value) => {
                  if (!value || value === "") {
                    return <span style={{ color: "#999" }}>Please select hiring manager</span>;
                  }
                  return <span>{hiringManagerName || "Please select hiring manager"}</span>;
                }}
              >
                <MenuItem value="" disabled>
                  Please select hiring manager
                </MenuItem>
                {hiringManager.length === 0 ? (
                  <MenuItem disabled>No Hiring Manager Available</MenuItem>
                ) : (
                  hiringManager?.map((manager) => (
                    <MenuItem key={manager.id} value={String(manager.id)}>
                      {manager.name}
                    </MenuItem>
                  ))
                )}
              </Select>
              <InputLabel
                style={{
                  color: "#f44336",
                  paddingTop: "3px",
                  fontSize: "12px",
                  paddingLeft: "10px",
                }}
              >
                {errors.hiringManagerId}
              </InputLabel>
            </Grid>
            <Grid my={2}></Grid>
            <Grid mt="25px"></Grid>
            <Typography
              color="#273848"
              fontWeight={700}
              lineHeight="14.94px"
              fontSize="15px"
              //   className="input-label-custom"
            >
              Overview
            </Typography>
            <Grid mt="25px"></Grid>
            <Grid item>
              <Typography className="input-label-custom">
                What is the specific job title for this role?{" "}
                <span style={{ color: "red" }}>*</span>
              </Typography>
              <Grid mb={1}></Grid>
              <VonqJobTitleField
                displayValue={jobTitle}
                onDisplayChange={handleJobTitleSelected}
                vonqLink={vonqMapping?.jobTitle}
                onVonqChange={(link) => updateVonqMapping({ jobTitle: link })}
                placeholder="Search and select a job title from the list"
                error={!!errors.jobTitle}
                helperText={
                  errors.jobTitle ||
                  (!vonqMapping?.jobTitle?.vonqId && jobTitle
                    ? "Please select a job title from the list."
                    : undefined)
                }
                textFieldProps={vonqFieldProps}
              />
            </Grid>
            <Grid my={2}></Grid>
            <Grid item>
              <Typography className="input-label-custom">
                What is the working location?{" "}
                <span style={{ color: "red" }}>*</span>
              </Typography>
              <Grid mb={1}></Grid>
              <VonqLocationField
                displayValue={workingLocation}
                onDisplayChange={(value) => {
                  setWorkingLocation(value);
                  if (value.trim() !== "") {
                    setErrors((prevErrors) => ({
                      ...prevErrors,
                      workingLocation: "",
                    }));
                  }
                }}
                vonqLink={vonqMapping?.location}
                onVonqChange={(link) =>
                  updateVonqMapping({
                    location: link,
                    workingLocationStructured: link?.description
                      ? {
                          city: link.description.split(",")[0]?.trim() || "",
                          country:
                            link.description.split(",").pop()?.trim() || "",
                        }
                      : undefined,
                  })
                }
                placeholder="Search for a city or location (e.g. Amsterdam)"
                error={!!errors.workingLocation}
                helperText={
                  errors.workingLocation ||
                  (!vonqMapping?.location?.vonqId && workingLocation
                    ? "Please select a location from the list."
                    : undefined)
                }
                textFieldProps={vonqFieldProps}
              />
            </Grid>
            <Grid my={2}></Grid>
            <Grid item>
              <Typography className="input-label-custom">
                Is working remotely possible for this role?{" "}
                <span style={{ color: "red" }}>*</span>
              </Typography>
              <Grid mb={1}></Grid>
              <Select
                labelId="work-location-ratio-select-label"
                id="work-location-ratio-select"
                value={remoteWorkChoice || ""}
                fullWidth
                displayEmpty
                onChange={(e) =>
                  handleRemoteWorkChange(e.target.value as "yes" | "no")
                }
                renderValue={(value) => {
                  if (!value) {
                    return (
                      <span style={{ color: "#999" }}>
                        Select whether remote work is allowed
                      </span>
                    );
                  }
                  const option = REMOTE_WORK_OPTIONS.find(
                    (item) => item.value === value
                  );
                  return <span>{option?.label || value}</span>;
                }}
              >
                <MenuItem value="" disabled>
                  Select whether remote work is allowed
                </MenuItem>
                {REMOTE_WORK_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              <InputLabel
                style={{
                  color: "#f44336",
                  paddingTop: "4px",
                  fontSize: "12px",
                  paddingLeft: "10px",
                }}
              >
                {errors.workLocationRatio}
              </InputLabel>
            </Grid>
            <Grid my={2}></Grid>
            <Grid item>
              <Typography className="input-label-custom">
                What is the reason why this position is vacant?{" "}
                <span style={{ color: "red" }}>*</span>
              </Typography>
              <Grid mb={1}></Grid>
              <Select
                labelId="vacant-type-select-label"
                id="vacant-type-select"
                value={vacancyReason || ""}
                fullWidth
                displayEmpty
                onChange={handleVacancyReason}
                renderValue={(value) => {
                  if (!value || value === "" || value === "Please select vacant postion") {
                    return <span style={{ color: "#999" }}>Please select vacant postion</span>;
                  }
                  return <span>{value}</span>;
                }}
              >
                <MenuItem value="" disabled>
                  Please select vacant postion
                </MenuItem>
                {vacantTypes?.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
              {vacancyReason === "Other" && (
                <TextField
                  fullWidth
                  sx={{ mt: 2 }}
                  value={otherVacancy}
                  onChange={(event) => {
                    setOtherVacancy(event.target.value);
                  }}
                  placeholder="Please specify"
                />
              )}
              <InputLabel
                style={{
                  color: "#f44336",
                  paddingTop: "4px",
                  fontSize: "12px",
                  paddingLeft: "10px",
                }}
              >
                {errors.vacancyReason}
              </InputLabel>
            </Grid>

            <Grid mt="25px"></Grid>
            <Typography
              color="#273848"
              fontWeight={700}
              lineHeight="14.94px"
              fontSize="15px"
              //   className="input-label-custom"
            >
              Contract type
            </Typography>
            <Grid mt="25px"></Grid>
            <Grid item>
              <Typography className="input-label-custom">
                What type of contract for this position?{" "}
                <span style={{ color: "red" }}>*</span>
              </Typography>
              <Grid mb={1}></Grid>
              <Select
                labelId="contract-type-select-label"
                id="contract-type-select"
                value={contractType || ""}
                fullWidth
                displayEmpty
                onChange={handleContractTypeChange}
                renderValue={(value) => {
                  if (!value || value === "" || value === "Please select position") {
                    return <span style={{ color: "#999" }}>Please select position</span>;
                  }
                  return <span>{value}</span>;
                }}
              >
                <MenuItem value="" disabled>
                  Please select position
                </MenuItem>
                {employmentTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.label}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <InputLabel
              style={{
                color: "#f44336",
                paddingTop: "4px",
                fontSize: "12px",
                paddingLeft: "10px",
              }}
            >
              {errors.contractType}
            </InputLabel>
            <Grid my={2}></Grid>
            <Grid item>
              <Typography className="input-label-custom">
                How many hours per week? <span style={{ color: "red" }}>*</span>
              </Typography>
              <Grid mb={1}></Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    From
                  </Typography>
                  <TextField
                    fullWidth
                    type="number"
                    inputProps={{ min: 0, max: 168 }}
                    className="input-box-custom"
                    placeholder="Enter hours from"
                    value={weeklyHoursFrom}
                    onChange={(e) => {
                      const value = e.target.value;
                      setWeeklyHoursFrom(value);
                      setHoursPerWeek(formatHoursPerWeekText(value, weeklyHoursTo));
                      if (value.trim() || weeklyHoursTo.trim()) {
                        setErrors((prev) => ({ ...prev, hoursPerWeek: "" }));
                      }
                    }}
                    error={!!errors.hoursPerWeek}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    To
                  </Typography>
                  <TextField
                    fullWidth
                    type="number"
                    inputProps={{ min: 0, max: 168 }}
                    className="input-box-custom"
                    placeholder="Enter hours to"
                    value={weeklyHoursTo}
                    onChange={(e) => {
                      const value = e.target.value;
                      setWeeklyHoursTo(value);
                      setHoursPerWeek(formatHoursPerWeekText(weeklyHoursFrom, value));
                      if (value.trim() || weeklyHoursFrom.trim()) {
                        setErrors((prev) => ({ ...prev, hoursPerWeek: "" }));
                      }
                    }}
                    error={!!errors.hoursPerWeek}
                  />
                </Grid>
              </Grid>
              <InputLabel
                style={{
                  color: "#f44336",
                  paddingTop: "4px",
                  fontSize: "12px",
                  paddingLeft: "10px",
                }}
              >
                {errors.hoursPerWeek}
              </InputLabel>
            </Grid>
            <Grid mt="25px"></Grid>
            <Typography
              color="#273848"
              fontWeight={700}
              lineHeight="14.94px"
              fontSize="15px"
              //   className="input-label-custom"
            >
              Salary and Benefits
            </Typography>
            <Grid mt="25px"></Grid>
            <Grid item>
              <Typography className="input-label-custom">
                What is the budgeted salary range for this position?{" "}
                <span style={{ color: "red" }}>*</span>
              </Typography>
              <Grid mb={1}></Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    From
                  </Typography>
                  <TextField
                    fullWidth
                    type="number"
                    inputProps={{ min: 0 }}
                    className="input-box-custom"
                    placeholder=">=0"
                    value={salaryFrom}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSalaryFrom(value);
                      setSalaryRange(
                        formatSalaryRangeText(
                          value,
                          salaryTo,
                          salaryPeriod,
                          salaryCurrency
                        )
                      );
                      if (salaryTo.trim()) {
                        setErrors((prev) => ({ ...prev, salaryRange: "" }));
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    To
                  </Typography>
                  <TextField
                    fullWidth
                    type="number"
                    inputProps={{ min: 0 }}
                    className="input-box-custom"
                    placeholder=">=0"
                    value={salaryTo}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSalaryTo(value);
                      setSalaryRange(
                        formatSalaryRangeText(
                          salaryFrom,
                          value,
                          salaryPeriod,
                          salaryCurrency
                        )
                      );
                      if (value.trim()) {
                        setErrors((prev) => ({ ...prev, salaryRange: "" }));
                      }
                    }}
                    error={!!errors.salaryRange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Currency {salaryTo.trim() ? <span style={{ color: "red" }}>*</span> : null}
                  </Typography>
                  <Autocomplete
                    fullWidth
                    options={Object.keys(keyValueForCurrency)}
                    value={
                      Object.entries(keyValueForCurrency).find(
                        ([, code]) => code === salaryCurrency
                      )?.[0] || null
                    }
                    onChange={(_, newValue) => {
                      const code = newValue
                        ? keyValueForCurrency[
                            newValue as keyof typeof keyValueForCurrency
                          ]
                        : "";
                      setSalaryCurrency(code);
                      setSalaryRange(
                        formatSalaryRangeText(
                          salaryFrom,
                          salaryTo,
                          salaryPeriod,
                          code
                        )
                      );
                      if (code) {
                        setErrors((prev) => ({ ...prev, salaryCurrency: "" }));
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select currency"
                        size="small"
                        className="input-box-custom"
                        error={!!errors.salaryCurrency}
                        helperText={errors.salaryCurrency}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Period
                  </Typography>
                  <Select
                    fullWidth
                    value={salaryPeriod || "yearly"}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSalaryPeriod(value);
                      setSalaryRange(
                        formatSalaryRangeText(
                          salaryFrom,
                          salaryTo,
                          value,
                          salaryCurrency
                        )
                      );
                    }}
                  >
                    {periodOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
              </Grid>
              <InputLabel
                style={{
                  color: "#f44336",
                  paddingTop: "4px",
                  fontSize: "12px",
                  paddingLeft: "10px",
                }}
              >
                {errors.salaryRange}
              </InputLabel>
            </Grid>
            <Grid my={2}></Grid>
            <Grid item>
              <Typography className="input-label-custom">
                Are there any additional benefits or perks associated with the
                role?
              </Typography>
              <Grid mb={1}></Grid>
              <OutlinedInput
                placeholder="Are there any additional benefits or perks associated with the role?"
                multiline
                minRows={4}
                maxRows={10}
                style={{ display: "inline-block", padding: "25px 11px" }}
                className="text-area-size"
                value={benefitsWithThisRole}
                onChange={(e) => {
                  const value = e.target.value;
                  setBenefitsWithThisRole(value);
                  if (value.trim() !== "") {
                    setErrors((prevErrors) => ({
                      ...prevErrors,
                      benefitsWithThisRole: "",
                    }));
                  }
                }}
              />
              <InputLabel
                style={{
                  color: "#f44336",
                  paddingTop: "3px",
                  fontSize: "12px",
                  paddingLeft: "10px",
                }}
              >
                {errors.benefitsWithThisRole}
              </InputLabel>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 0.5, pl: 1 }}
              >
                Prefilled from your company profile. You can add role-specific
                benefits here.
              </Typography>
            </Grid>
            <Grid my={2}></Grid>
            <Grid
              container
              direction="row"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Grid xs={2}></Grid>
              <Grid
                item
                // xs={isMobile ? 0 : 3}
                xs={isTabs ? 0 : mode ? 3 : 2}
                style={{ maxWidth: "fitContent" }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  style={{
                    backgroundColor: "#84E1CC",
                    boxShadow: "none",
                    borderRadius: "5px",
                    padding: loading ? "7px" : "17px 17px",
                    lineHeight: "0px",
                    textTransform: "none",
                    letterSpacing: "0px",
                  }}
                  onClick={(event) => handleSubmit(event)}
                >
                  {mode ? loading ? <Loader /> : "Save & Next" : "Continue"}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Container>
      <LoadingOverlay loading={aiLoading} />
      <ConfirmationDialog
        open={openDialog}
        onConfirm={handleDialogConfirm}
        onCancel={handleDialogCancel}
        title="Confirmation"
        message="A similar job title already exists. Do you want to use the details of the existing job?"
        cancelButtonName="Cancel"
        confirmButtonName="Confirm"
      />
      <ConfirmationDialog
        open={confirmDialogOpen}
        onConfirm={handleConfirmGenerate}
        onCancel={() => setConfirmDialogOpen(false)}
        title="Confirmation"
        message="Do you want to generate AI-based job details?"
        cancelButtonName="Cancel"
        confirmButtonName="Yes, Generate"
      />
    </div>
  );
};

export default JobProfileFirst;
